import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Chat } from "enums/ChatType";
import { getChats as fetchChats } from "services/SauceService";

export interface ChatState {
  chat: PartialChatState;
}

interface PartialChatState {
  loading: boolean;
  totalUnreadCount: number;
  chats: Chat[];
}

const initialState: Partial<PartialChatState> = {
  loading: true,
  totalUnreadCount: 0,
  chats: [],
};

export const getAllChats = createAsyncThunk(
  "chat/getAllChats",
  async ({ countOnly, sortBy, chatType }: { countOnly?: boolean; sortBy?: "newest" | "oldest"; chatType?: "all" | "business" | "influencer" | "unreplied" } = {}) => {
    const businessId = localStorage.getItem("businessId");
    if (!businessId) {
      return {};
    }
    const chats: any = await fetchChats({ businessId, countOnly, sortBy, chatType });

    const businessChat = chats?.chats?.find(
      (chat: Chat) => chat.context?.type === "business-mustard"
    );

    if (businessChat && Array.isArray(chats.chats)) {
      chats.chats = [
        businessChat,
        ...chats.chats.filter((c: Chat) => c._id !== businessChat._id),
      ];
    }

    return chats;
  }
);

export const loadMoreChats = createAsyncThunk(
  "chat/loadMoreChats",
  async ({ sortBy, chatType }: { sortBy?: "newest" | "oldest"; chatType?: "all" | "business" | "influencer" | "unreplied" } = {}, { getState }) => {
    const businessId = localStorage.getItem("businessId");
    if (!businessId) {
      return {};
    }
    
    const state = getState() as { chat: PartialChatState };
    const currentChats = state.chat.chats || [];
    
    // Calculate offset based on current chats length
    const offset = currentChats.length;
    
    const chats: any = await fetchChats({ 
      businessId, 
      countOnly: false,
      offset,
      limit: 3,
      sortBy,
      chatType
    });

    const businessChat = chats?.chats?.find(
      (chat: Chat) => chat.context?.type === "business-mustard"
    );

    let newChats = chats?.chats || [];
    if (businessChat && Array.isArray(newChats)) {
      newChats = [
        businessChat,
        ...newChats.filter((c: Chat) => c._id !== businessChat._id),
      ];
    }

    return {
      chats: newChats,
      pagination: chats?.pagination,
      append: true // Flag to indicate this should append to existing chats
    };
  }
);

// New thunk for adding a new chat
export const addNewChat = createAsyncThunk(
  "chat/addNewChat",
  async (newChat: Chat, { getState }) => {
    const state = getState() as { chat: PartialChatState };
    // Return new array with the new chat at the beginning
    return {
      chats: [newChat, ...(state.chat.chats || [])],
      totalUnreadCount: (state.chat.totalUnreadCount || 0) + 1,
    };
  }
);

export const setUnreadMessageCount = createAsyncThunk(
  "chat/setUnreadMessageCount",
  async (unreadCount: number) => {
    return { totalUnreadCount: unreadCount };
  }
);

export const setUnreadCountForChat = createAsyncThunk(
  "chat/setUnreadCountForChat",
  async (
    { chatId, unreadCount }: { chatId: string; unreadCount: number },
    { getState }
  ) => {
    const state = getState() as { chat: PartialChatState };
    const chatIndex = state.chat.chats?.findIndex(
      (chat) => chat._id === chatId
    );
    if (chatIndex === -1) return;

    const newChat = {
      ...state.chat.chats?.[chatIndex],
      unreadCount,
    };

    // replace the old chat with the new one at the index
    const newChats = [...state.chat.chats];
    newChats[chatIndex] = newChat;

    return {
      chats: newChats,
    };
  }
);

export const deleteMessage = createAsyncThunk(
  "chat/deleteMessage",
  async (
    { chatId, messageId }: { chatId: string; messageId: string },
    { getState }
  ) => {
    const state = getState() as { chat: PartialChatState };
    const chatIndex = state.chat.chats?.findIndex(
      (chat) => chat._id === chatId
    );

    const chat = state.chat.chats?.[chatIndex];
    if (!chat) return;

    const clonedChat = { ...chat };
    clonedChat.messages = chat.messages.filter(
      (message) => message._id !== messageId
    );
    clonedChat.unreadCount = chat.unreadCount > 0 ? chat.unreadCount - 1 : 0;

    const newState = [...(state.chat.chats || [])];
    newState[chatIndex] = clonedChat;

    return { chats: newState };
  }
);

export const addNewMessage = createAsyncThunk(
  "chat/addNewMessage",
  async (newMessage: Chat["messages"][0], { getState }) => {
    console.log("adding new message");
    const state = getState() as { chat: PartialChatState };

    const chatIndex = state.chat.chats?.findIndex(
      (chat) => chat._id === newMessage.chatId
    );

    const chat = state.chat.chats?.[chatIndex];

    console.log("chat", chat);
    if (!chat) return;

    const clonedChat = { ...chat };
    clonedChat.messages = [...chat.messages, newMessage];
    clonedChat.unreadCount = chat.unreadCount + 1;

    const newState = [...(state.chat.chats || [])];
    newState[chatIndex] = clonedChat;

    return {
      chats: newState,
    };
  }
);

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllChats.fulfilled, (state, action) => {
      state.chats = action.payload.chats;
      state.totalUnreadCount = action.payload.totalUnreadCount;
      state.loading = false;
    });

    builder.addCase(getAllChats.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getAllChats.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(loadMoreChats.fulfilled, (state, action) => {
      if (action.payload?.chats && action.payload.append) {
        // Append new chats to existing ones
        state.chats = [...(state.chats || []), ...action.payload.chats];
      }
    });

    builder.addCase(loadMoreChats.pending, (state) => {
      // Don't set loading to true for load more to avoid showing main spinner
    });

    builder.addCase(addNewChat.fulfilled, (state, action) => {
      if (action.payload?.chats) {
        state.chats = action.payload?.chats;
        state.totalUnreadCount = action.payload.totalUnreadCount;
      } else if (!state.chats || state.chats.length === 0) {
        state.chats = [];
      }
    });

    builder.addCase(setUnreadMessageCount.fulfilled, (state, action) => {
      state.totalUnreadCount = action.payload.totalUnreadCount;
    });

    builder.addCase(addNewMessage.fulfilled, (state, action) => {
      console.log("addNewMessage.fulfilled", action.payload);
      if (action.payload?.chats) {
        state.chats = action.payload?.chats;
      } else if (!state.chats || state.chats.length === 0) {
        state.chats = [];
      }
    });

    builder.addCase(deleteMessage.fulfilled, (state, action) => {
      console.log("deleteMessage.fulfilled", action.payload);
      if (action.payload?.chats) {
        state.chats = action.payload?.chats;
      } else if (!state.chats || state.chats.length === 0) {
        state.chats = [];
      }
      state.chats = action.payload?.chats || [];
    });

    builder.addCase(setUnreadCountForChat.fulfilled, (state, action) => {
      if (action.payload?.chats) {
        state.chats = action.payload?.chats;
      } else if (!state.chats || state.chats.length === 0) {
        state.chats = [];
      }
    });
  },
});

export default chatSlice.reducer;
